<template>
    <form-wrapper :sections="stickyNavSections" :hideNav="Boolean(props.contactId) || stickyNavSections.length == 1">
        <vx-alert v-if="groupId && onCorporate()" color="info" :closeable="false" shadow>
            <template v-slot:icon>
                <font-awesome-icon widthAuto :icon="faGrid2Plus"></font-awesome-icon>
            </template>

            <div class="flex items-center justify-between">
                <div>
                    Contact will be in <strong>{{ group?.name || groupId }}</strong>
                </div>
                <vx-button :href="`/settings/teams/${teamId}/groups/${groupId}/contacts`" color="info" size="sm">
                    Back to Group
                    <font-awesome-icon widthAuto :icon="faSquareArrowUpLeft"></font-awesome-icon>
                </vx-button>
            </div>
        </vx-alert>
        <contact-form-details :contactId="props.contactId"></contact-form-details>
        <vx-expandable-card v-if="!managesRelationships && props.contactId">
            <template v-slot:content>
                <contact-view-relationships></contact-view-relationships>
            </template>
        </vx-expandable-card>
        <contact-form-subscriptions v-if="managesRelationships"></contact-form-subscriptions>
        <contact-form-tags v-if="managesRelationships"></contact-form-tags>
        <contact-form-groups v-if="FEATURES.contact_groups && !groupId"></contact-form-groups>
        <contact-form-custom-attributes v-if="managesRelationships"></contact-form-custom-attributes>

        <vx-sticky-footer>
            <router-button :to="{ name: 'contacts.list' }" class="w-20" size="lg" color="muted"> Cancel </router-button>
            <vx-button
                @click="submit()"
                data-test="contact-save"
                class="grow"
                size="lg"
                color="primary"
                :loading="processing"
            >
                {{ props.contactId ? 'Update' : 'Create' }} Contact
            </vx-button>
        </vx-sticky-footer>
    </form-wrapper>
</template>

<script setup>
import { VxAlert, VxStickyFooter, VxButton, VxExpandableCard } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faGrid2Plus, faSquareArrowUpLeft } from '@fortawesome/pro-solid-svg-icons';
import { inject, ref, computed, watch } from 'vue';
import ContactFormDetails from './ContactFormDetails.vue';
import ContactFormSubscriptions from './ContactFormSubscriptions.vue';
import ContactFormTags from './ContactFormTags.vue';
import ContactFormGroups from './ContactFormGroups.vue';
import ContactFormCustomAttributes from './ContactFormCustomAttributes.vue';
import ContactViewRelationships from '../view/ContactViewRelationships.vue';
import RouterButton from '~/components/general/RouterButton.vue';
import { useContactDetails } from '../useContactDetails';
import { useFormSubmission } from '~/composables/useFormSubmission';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { groupsClient } from '~/services';
import FormWrapper from '~/components/general/FormWrapper.vue';

const teamId = Spark.state.currentTeam.id;
const FEATURES = inject('FEATURES');

const { onCorporate } = useCurrentGroup();

const groupId = inject('contactGroupId', ref(undefined));

const group = ref();

if (groupId.value && onCorporate()) {
    groupsClient.getGroup({ teamId }, groupId.value).then((response) => {
        group.value = response.data;
    });
}

const props = defineProps({
    contactId: {
        type: Number,
        required: false,
    },
});

const {
    contact,
    unmount,

    save,
    saveSubscriptions,
    syncGroups,
    syncCustomAttributes,
    syncTags,

    getStickyPhoneNumber,
    getSubscriptions,
    getGroups,
    getTags,
    getCustomAttributes,
} = useContactDetails();

watch(
    () => contact.value.id,
    (id) => {
        if (!id) {
            return;
        }
        getStickyPhoneNumber();
        getSubscriptions();
        getGroups();
        getTags();
        getCustomAttributes();
    },
    { immediate: true }
);

const { submit, processing } = useFormSubmission({
    submit: async () => {
        await save();
        await Promise.all([saveSubscriptions(), syncGroups(), syncCustomAttributes(), syncTags()]);
    },
    preserveScroll: () => props.contactId,
    reset: () => {
        if (!props.contactId) {
            unmount({ loading: false });
        }
    },
    notification: () => ({
        message: `Contact ${props.contactId ? 'updated' : 'created'}`,
        action:
            groupId.value && onCorporate()
                ? {
                      icon: faSquareArrowUpLeft,
                      label: 'Back to Group',
                      href: `/settings/teams/${teamId}/groups/${groupId.value}/contacts`,
                  }
                : {
                      icon: faSquareArrowUpLeft,
                      label: 'Back to Contacts',
                      to: { name: 'contacts.list' },
                  },
    }),
});

const stickyNavSections = computed(() =>
    [
        { id: 'details', label: 'Details' },
        managesRelationships.value && { id: 'subscriptions', label: 'Sub Status' },
        managesRelationships.value && { id: 'tags', label: 'Tags' },
        FEATURES.contact_groups && !groupId.value ? { id: 'groups', label: 'Groups' } : null,
        managesRelationships.value && { id: 'custom-attributes', label: 'Custom Attr' },
    ].filter((s) => !!s)
);

const managesRelationships = computed(() => {
    if (groupId.value && !props.contactId) {
        return false;
    }

    if (groupId.value && props.contactId && !contact.value.is_preferred) {
        return false;
    }

    return true;
});
</script>
